// =============================================================================
// Phase 1 SOC / Wazuh Integration: Link Discovery to Endpoint Dialog
// =============================================================================

import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSecurityStore } from '@/stores/securityStore';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Link2, Search } from 'lucide-react';
import { toast } from 'sonner';
import type { DiscoveredIdentity } from '@/types/security';

interface DiscoveryLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  discovery: DiscoveredIdentity;
}

interface Endpoint {
  id: string;
  name: string;
  type: string;
  ip: string | null;
  mac: string | null;
  site_id: string | null;
  site?: { name: string } | null;
}

export function DiscoveryLinkDialog({ open, onOpenChange, discovery }: DiscoveryLinkDialogProps) {
  const { profile } = useAuth();
  const orgId = profile?.org_id;
  
  const { linkDiscovery } = useSecurityStore();
  
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fetch endpoints on open
  useEffect(() => {
    if (open && orgId) {
      fetchEndpoints();
    }
  }, [open, orgId]);
  
  const fetchEndpoints = async () => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('endpoints')
        .select('id, name, type, ip, mac, site_id, site:sites(name)')
        .eq('org_id', orgId)
        .order('name');
      
      if (error) throw error;
      setEndpoints(data || []);
    } catch (err) {
      toast.error('Failed to load endpoints');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter endpoints by search
  const filteredEndpoints = endpoints.filter((e) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      e.ip?.toLowerCase().includes(q) ||
      e.mac?.toLowerCase().includes(q) ||
      e.site?.name?.toLowerCase().includes(q)
    );
  });
  
  // Handle link
  const handleLink = async () => {
    if (!selectedEndpoint) {
      toast.error('Please select an endpoint');
      return;
    }
    
    setLinking(true);
    try {
      await linkDiscovery(discovery.id, selectedEndpoint);
      toast.success('Discovery linked to endpoint');
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to link discovery');
    } finally {
      setLinking(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Link Discovery to Endpoint
          </DialogTitle>
          <DialogDescription>
            Link this discovered network identity to an existing endpoint in your asset inventory.
          </DialogDescription>
        </DialogHeader>
        
        {/* Discovery Info */}
        <div className="rounded-lg border p-3 bg-muted/50 space-y-1">
          <div className="text-sm">
            <span className="text-muted-foreground">IP:</span>{' '}
            <span className="font-mono">{discovery.ip || '-'}</span>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">MAC:</span>{' '}
            <span className="font-mono">{discovery.mac || '-'}</span>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Hostname:</span>{' '}
            {discovery.hostname || '-'}
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Source:</span>{' '}
            <Badge variant="outline" className="text-xs">{discovery.source}</Badge>
          </div>
        </div>
        
        {/* Endpoint Selection */}
        <div className="space-y-3">
          <Label>Select Endpoint</Label>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search endpoints..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          {/* Endpoint list */}
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Select value={selectedEndpoint} onValueChange={setSelectedEndpoint}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an endpoint..." />
              </SelectTrigger>
              <SelectContent>
                {filteredEndpoints.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    No endpoints found
                  </div>
                ) : (
                  filteredEndpoints.map((endpoint) => (
                    <SelectItem key={endpoint.id} value={endpoint.id}>
                      <div className="flex flex-col">
                        <span>{endpoint.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {endpoint.type}
                          {endpoint.ip && ` · ${endpoint.ip}`}
                          {endpoint.site?.name && ` · ${endpoint.site.name}`}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleLink} disabled={linking || !selectedEndpoint}>
            {linking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Link Endpoint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
